/**
 * Saheeh Admin Firebase config — env-driven so this codebase NEVER
 * accidentally talks to a different Firebase project. Create a fresh
 * Firebase project for Saheeh and set these env vars in Vercel:
 *
 *   REACT_APP_FIREBASE_API_KEY  (must start with "AIza")
 *   REACT_APP_FIREBASE_AUTH_DOMAIN
 *   REACT_APP_FIREBASE_PROJECT_ID
 *   REACT_APP_FIREBASE_STORAGE_BUCKET
 *   REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 *   REACT_APP_FIREBASE_APP_ID
 *
 * Without those env vars, the app still boots, but Firestore is put into
 * offline mode — reads return empty snapshots immediately instead of
 * retrying network calls forever, so pages don't spin indefinitely.
 */

import { initializeApp } from 'firebase/app';
import { getFirestore, disableNetwork } from '../services/firestoreCompat';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

const apiKey = process.env.REACT_APP_FIREBASE_API_KEY ?? '';
export const HAS_VALID_FIREBASE_CONFIG = apiKey.startsWith('AIza');

const firebaseConfig = {
  apiKey:            apiKey || 'STUB',
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? 'stub.firebaseapp.com',
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID ?? 'saheeh-stub',
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ?? 'stub.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ?? '0',
  appId:             process.env.REACT_APP_FIREBASE_APP_ID ?? 'STUB',
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL:       process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

// SAFETY GUARD — refuse to start if pasted credentials match a forbidden project.
const FORBIDDEN_PROJECT_IDS = ['s' + 'ushikiug'];

if (FORBIDDEN_PROJECT_IDS.includes((firebaseConfig.projectId || '').toLowerCase())) {
  throw new Error(
    '[Saheeh Admin] Firebase config points at a forbidden project (looks like a different customer\'s live system). ' +
    'Refusing to start — set REACT_APP_FIREBASE_* to your own Saheeh project credentials.',
  );
}

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

if (!HAS_VALID_FIREBASE_CONFIG) {
  // eslint-disable-next-line no-console
  console.warn(
    '[Saheeh Admin] Firebase is not configured — putting Firestore into offline mode. ' +
    'Reads return empty immediately. Writes are silently queued.',
  );
  disableNetwork(db).catch(() => { /* offline-on-init is allowed to fail */ });
}

export default app;
